import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { PurchasePageDataProvider, PurchasePageProduct } from '../../../plansAndPurchaseTab/types/types';
import { NoExtendableProductsReason, SwitchValue } from '../../types';
import { debounce } from '../../../../../../utils/event.utils';
import { translateFn } from '../../../../../../utils/translator.helper';
import { ButtonColors, ButtonTypes } from '../../../../../../ui/gzButton/types';
import { FontSizes, FontWeights, LabelWidthValues } from '../../../../../../types/common/types';
import { SwitchConfig } from '../../../../../../ui/gzLabelSwitch/types';
import { SupportUrls } from '../../../../../../ui/gzHelpAndSupport/utils/supportUrls';
import { PurchaseCardConfig } from '../../gzPurchaseCardLayout/types';
import {
  BUY_NEW_LICENSE_WINBACK_LINKS,
  MAX_SEATS_EXCEEDED_PRODUCT_PAGES,
  RENEWAL_DAYS_UNTIL_AVAILABLE,
} from '../../constants';
import moment from 'moment-timezone';
import { AvailableInProductTrial } from '../../../../../../productTrials/b2b/inProductTrialsApp/types/AvailableInProductTrialsDetails';
import { getHomePageSecondaryCardItemsConfig } from '../../../../../../productTrials/b2b/inProductTrialsApp/view/gzPageConfigs/gzHomePageConfig/gz-home-page-main-product-items-config';

@customElement('gz-purchase-page')
export class GzPurchasePageWebComponent extends LitElement {
  static get styles() {
    return styles;
  }

  @property()
  public products: PurchasePageProduct[];

  @property()
  public purchasableProducts: AvailableInProductTrial[];

  /**
   * Use case for this property:
   * User clicks button in licensing grid to upgrade / renew a license, but the products are not
   * loaded yet, in Purchase tab. The product will be selected after data loads.
   */
  public productIdToSelectAfterLoad?: {
    productId: PurchasePageProduct['productId'];
    switchValue: SwitchValue;
  };

  @property({ type: Object, attribute: true, reflect: true })
  public selectedProduct: PurchasePageProduct;

  @state()
  private switchValue = SwitchValue.ExtendSeats;

  @state()
  private seatsValue = 1;

  @state()
  private loadingExtendSeatsPrice = false;

  @state()
  private loadingBuyButton = false;

  @state()
  private showRefreshAfterPurchaseInfoBox = false;

  @state()
  private maxSeatsAlreadyExceeded = false;

  @state()
  private showTextForMaxSeatsReached = false;

  @state()
  private noExtendableProductsReason?: NoExtendableProductsReason;

  public purchasePageDataProvider: PurchasePageDataProvider;

  /**
   * This is a counter variable that is incremented every time a request is made to fetch the price for seat upgrades.
   * It is used to ignore the results of previous requests. This is particularly useful when multiple requests are
   * initiated at roughly the same time. Even if a previous request finishes last, its result will be ignored,
   * ensuring that the data used is always from the most recent request.
   */
  private lastFetchSeatsPriceRequestId = 0;

  private fetchAndUpdateSeatsPriceFn = this.createFetchAndUpdateSeatsPriceFn();
  private fetchAndUpdateSeatsFnDebounced = debounce(this.fetchAndUpdateSeatsPriceFn, 1000);

  render(): TemplateResult {
    if (this.products === undefined) {
      return html`
        <div class="loading-container">
          <gz-progress-spinner size="large"></gz-progress-spinner>
        </div>
      `;
    }

    return html`
      ${this.renderExpandCurrentPlanSection()}
      <!-- -->
      ${this.renderPurchasableProductsSection()}
    `;
  }

  private renderExpandCurrentPlanSection(): TemplateResult | undefined {
    return html`
      <gz-title
        class="mb-1 mt-2"
        fontSize="${FontSizes.HEADING_4}"
        fontWeight="${FontWeights.MEDIUM}"
        automationId="ipb-expand-current-plan-title"
      >
        ${translateFn('Expand current plan')}
      </gz-title>

      ${this.noExtendableProductsReason
        ? this.renderNoExtendableProductsPanel()
        : html`
            ${this.renderAfterPurchaseInfoBox()}

            <div class="d-flex flex-row">
              <div class="d-flex flex-column expand-plan-section">
                <div class="d-flex flex-column mt-1">
                  ${this.renderProductsDropdown()}
                  <!--            -->
                  ${this.renderSwitch()}
                </div>

                ${this.renderAccountInfo()}
                <!--            -->
                ${this.renderAssistanceInfo()}
              </div>
              <div>${this.renderPurchaseCard()}</div>
            </div>
          `}
    `;
  }

  private renderPurchasableProductsSection(): TemplateResult | undefined {
    if (this.purchasableProducts && this.purchasableProducts.length === 0) {
      return;
    }

    return html`
      <gz-title
        class="mb-1 mt-3"
        fontSize="${FontSizes.HEADING_4}"
        fontWeight="${FontWeights.MEDIUM}"
        automationId="ipb-buy-compatible-product-title"
      >
        ${translateFn('Buy compatible products')}
      </gz-title>

      <gz-trial-home-page .cardItems=${getHomePageSecondaryCardItemsConfig(this.purchasableProducts)} />
    `;
  }

  private renderAfterPurchaseInfoBox(): TemplateResult | undefined {
    if (!this.showRefreshAfterPurchaseInfoBox) {
      return undefined;
    }

    return html`
      <gz-info-box class="mb-1" automationId='ipb-after-purchase-info-box'>
        ${translateFn(
          'Your changes will apply shortly after the purchase. Refresh your browser page to update the displayed information.'
        )}
        </br>
        <gz-button
          type="${ButtonTypes.QUIET}"
          color="${ButtonColors.PRIMARY}"
          class="mt-1"
          buttonheight="small"
          buttonfontsize="small"
          automationId='ipb-after-purchase-info-box-reload-button'
          @click="${() => window.location.reload()}"
        >
          ${translateFn('Refresh page')}
        </gz-button>
      </gz-info-box>
    `;
  }

  private renderNoExtendableProductsPanel(): TemplateResult | undefined {
    if (!this.noExtendableProductsReason) {
      return;
    }

    return html`
      <gz-text fontSize="${FontSizes.PARAGRAPH_12}" automationId="ipb-no-extendable-products-reason">
        ${this.translateNoExtendableProductsReason(this.noExtendableProductsReason)}
      </gz-text>

      ${this.noExtendableProductsReason === NoExtendableProductsReason.DisabledSubscription
        ? html`
            <gz-button
              type="${ButtonTypes.OUTLINE}"
              color="${ButtonColors.SECONDARY}"
              class="mt-2"
              buttonheight="small"
              buttonfontsize="small"
              automationId="ipb-account-details-button"
              @click="${() => window.open('https://store.bitdefender.com/myaccount/', '_blank')}"
            >
              ${translateFn('Go to billing account')}
            </gz-button>
          `
        : ''}
    `;
  }

  private renderProductsDropdown(): TemplateResult {
    const productDropdownOptions = this.products.map(product => ({
      title: product.productName,
      value: product.productId,
    }));

    return html`
      <gz-dropdown
        label=${translateFn('Product Name')}
        labelWidth="${LabelWidthValues.FULL}"
        class="pb-2"
        automationId="ipb-products-dropdown"
        .options=${productDropdownOptions}
        .value=${this.selectedProduct.productId}
        @optionSelected=${this.handleProductChanged}
      ></gz-dropdown>
    `;
  }

  private renderSwitch(): TemplateResult {
    const itemLeft: SwitchConfig = {
      label: `${translateFn('Add seats')}`,
      value: SwitchValue.ExtendSeats,
      checked: this.switchValue === SwitchValue.ExtendSeats,
      disabled: this.selectedProduct.isExpired,
      disabledTooltipText: translateFn('You require an active license to purchase additional seats.'),
    };
    const itemRight: SwitchConfig = {
      label: `${translateFn('Renew')}`,
      value: SwitchValue.ExtendPeriod,
      checked: this.switchValue === SwitchValue.ExtendPeriod,
    };

    return html`
      <gz-text fontSize="${FontSizes.PARAGRAPH_12}" automationId="ipb-switch-explanation-text">
        ${translateFn('Renew or add seats to your current license.')}
      </gz-text>

      <gz-label-switch
        class="mt-3 mb-3 label-switch"
        automationId="ipb-switch"
        .itemLeft=${itemLeft}
        .itemRight=${itemRight}
        @labelSwitchChanged="${this.handleSwitchChanged}"
      >
      </gz-label-switch>

      ${this.renderSwitchContent()}
    `;
  }

  private renderSwitchContent(): TemplateResult | undefined {
    switch (this.switchValue) {
      case SwitchValue.ExtendSeats:
        return this.renderSwitchContentExtendSeats();

      case SwitchValue.ExtendPeriod:
        return this.renderSwitchContentExtendPeriod();
    }
  }

  private renderSwitchContentExtendSeats(): TemplateResult | undefined {
    const minSeats = this.getMinSeats();

    if (this.maxSeatsAlreadyExceeded) {
      return undefined;
    }

    return html`
      <gz-labeled-content
        class="mt-1 mb-2"
        label="${translateFn('Increase seats by')}"
        automationId="ipb-extend-seats-switch-labeled-content"
      >
        <gz-increment-input
          automationId="ipb-extend-seats-switch-increment-input"
          .value=${this.seatsValue}
          .inputConfig=${{
            min: minSeats,
            max: this.getMaxSeats(),
          }}
          .incrementBtnValues=${[5, 10, 20]}
          @incrementInputChange=${this.handleSeatsChange}
          @maxValueReached=${this.handleMaxValueReached}
        ></gz-increment-input>
      </gz-labeled-content>

      <gz-text fontSize="${FontSizes.PARAGRAPH_12}" automationId="ipb-extend-seats-switch-explanation-text">
        ${translateFn(
          'Select the number of extra endpoints you want to protect. Your license can have up to {MAX_SEATS} seats.',
          {
            MAX_SEATS: this.selectedProduct.maxSeats,
          }
        )}
      </gz-text>
      ${this.selectedProduct.serversPercentage
        ? html`
            <gz-text
              fontSize="${FontSizes.PARAGRAPH_12}"
              automationId="ipb-extend-seats-switch-explanation-text-servers"
            >
              ${translateFn('Servers account for up to {SERVER_PERCENTAGE}% of total devices.', {
                SERVER_PERCENTAGE: this.getServerSeatsPercentage(),
              })}
            </gz-text>
          `
        : ''}
      ${this.showTextForMaxSeatsReached ? this.renderMaxSeatsAlreadyExceededText() : ''}
    `;
  }

  private renderSwitchContentExtendPeriod(): TemplateResult {
    return html`
      <gz-labeled-content
        class="mt-1 mb-2 number-of-years"
        label="${translateFn('Number of years')}"
        automationId="ipb-extend-period-switch-labeled-content"
      >
        <gz-input-number
          automationId="ipb-extend-period-switch-years-input"
          .readonly=${true}
          .value=${this.selectedProduct.billingCycleYears}
        ></gz-input-number>
      </gz-labeled-content>
      <gz-text fontSize="${FontSizes.PARAGRAPH_12}" automationId="ipb-extend-period-switch-explanation-text">
        ${translateFn(
          'This is the validity of your original license. It will be added to your expiration date upon renewal.'
        )}
      </gz-text>
    `;
  }

  private renderAccountInfo(): TemplateResult | undefined {
    if (this.switchValue !== SwitchValue.ExtendPeriod) {
      return undefined;
    }

    return html`
      <div class="d-flex flex-column mt-3">
        <gz-title
          class="mb-1 mt-2"
          fontSize="${FontSizes.HEADING_6}"
          fontWeight="${FontWeights.MEDIUM}"
          automationId="ipb-account-details-title"
        >
          ${translateFn('Billing account details')}
        </gz-title>
        <gz-text fontSize="12" automationId="ipb-account-details-text">
          ${translateFn('Manage your renewal preferences and billing information from your billing account.')}
        </gz-text>
        <gz-button
          type="${ButtonTypes.OUTLINE}"
          color="${ButtonColors.SECONDARY}"
          class="mt-2"
          buttonheight="small"
          buttonfontsize="small"
          automationId="ipb-account-details-button"
          @click="${() => window.open('https://store.bitdefender.com/myaccount/', '_blank')}"
        >
          ${translateFn('Go to billing account')}
        </gz-button>
      </div>
    `;
  }

  private renderAssistanceInfo(): TemplateResult {
    return html`
      <div class="d-flex flex-column mt-3">
        <gz-title
          class="mb-1 mt-2"
          automationId="ipb-technical-support-title"
          .fontSize=${FontSizes.HEADING_6}
          .fontWeight=${FontWeights.MEDIUM}
        >
          ${translateFn('Technical Support')}
        </gz-title>
        <gz-text automationId="ipb-technical-support-text" .fontSize=${12}>
          ${translateFn(
            'If you need assistance, please open a support ticket. Our specialists will take care of your case as soon as possible.'
          )}
        </gz-text>
        <gz-link
          class="mt-1 no-padding"
          automationId="ipb-technical-support-link"
          .href=${SupportUrls.TECHNICAL_ASSISTANCE}
        >
          <gz-text .fontSize=${FontSizes.PARAGRAPH_12}>${translateFn('Open support ticket')}</gz-text>
        </gz-link>
      </div>
    `;
  }

  private renderPurchaseCard(): TemplateResult | undefined {
    const config = this.computeConfigForPurchaseCard();

    return html`<gz-purchase-card-layout .config=${config}></gz-purchase-card-layout>`;
  }

  private computeConfigForPurchaseCard(): PurchaseCardConfig {
    const selectedProduct = this.selectedProduct;
    const currentPlanTitle = translateFn('Current license');

    switch (this.switchValue) {
      case SwitchValue.ExtendSeats:
        return this.computeConfigForPurchaseCardExtendSeats(selectedProduct, currentPlanTitle);

      case SwitchValue.ExtendPeriod:
        return this.computeConfigForPurchaseCardExtendPeriod(selectedProduct, currentPlanTitle);
    }
  }

  private computeConfigForPurchaseCardExtendSeats(
    selectedProduct: PurchasePageProduct,
    currentPlanTitle: string
  ): PurchaseCardConfig {
    const totalSeats = this.getTotalSeats();

    return {
      title: selectedProduct.productName,
      productId: selectedProduct.productId,
      currentPlan: {
        title: currentPlanTitle,
        seats: selectedProduct.seats,
        serverSeats: selectedProduct.serverSeats,
        availableUntil: selectedProduct.expiryDate,
      },
      newPlan: {
        title: translateFn('Updated license'),
        seats: totalSeats,
        serverSeats: this.computeServerSeats(totalSeats),
        availableUntil: selectedProduct.expiryDate,
        inputChanges: {
          seatChange: selectedProduct.seats !== totalSeats,
          serverChange: selectedProduct.seats !== totalSeats,
          dateChange: selectedProduct.expiryDate !== selectedProduct.expiryDate,
        },
      },
      priceConfig: {
        totalPrice: this.selectedProduct.extendSeatsPrice || 0,
        currency: selectedProduct.currency,
        loading: this.loadingExtendSeatsPrice,
      },
      buyButton: {
        title: translateFn('Proceed to checkout'),
        loading: this.loadingBuyButton,
        buyEventHandlerFn: this.handleExtendSeatsBuyEvent.bind(this),
      },
      maxSeats: selectedProduct.maxSeats,
      maxSeatsAlreadyExceeded: this.maxSeatsAlreadyExceeded,
    };
  }

  private computeConfigForPurchaseCardExtendPeriod(
    selectedProduct: PurchasePageProduct,
    currentPlanTitle: string
  ): PurchaseCardConfig {
    const newAvailableUntil = moment(selectedProduct.expiryDate)
      .add(selectedProduct.billingCycleYears, 'years')
      .toDate();
    const differenceInDays = moment(selectedProduct.expiryDate).diff(moment(), 'days');
    const renewalNotAvailable = differenceInDays > RENEWAL_DAYS_UNTIL_AVAILABLE;

    const config: PurchaseCardConfig = {
      title: selectedProduct.productName,
      productId: selectedProduct.productId,
      currentPlan: {
        title: currentPlanTitle,
        seats: selectedProduct.seats,
        serverSeats: selectedProduct.serverSeats,
        years: selectedProduct.billingCycleYears,
        availableUntil: selectedProduct.expiryDate,
      },
      newPlan: {
        title: translateFn('Renewed license'),
        seats: selectedProduct.seats,
        serverSeats: selectedProduct.serverSeats,
        years: selectedProduct.billingCycleYears,
        availableUntil: newAvailableUntil,
        inputChanges: {
          dateChange: selectedProduct.expiryDate !== newAvailableUntil,
        },
      },
      buyButton: {
        title: translateFn('Renew now'),
        loading: this.loadingBuyButton,
        buyEventHandlerFn: this.handleExtendPeriodBuyEvent.bind(this),
      },
      autorenewal: selectedProduct.autoRenewalEnabled,
      discountText: '*' + translateFn('different discounts may apply on manual renewal.'),
    };

    if (renewalNotAvailable) {
      config.renewalNotAvailableYetText =
        '*' + translateFn("Manual renewal will become available 370 days before your license's expiration date.");
    }

    if (selectedProduct.hasRenewalGracePeriodPassed) {
      config.showBuyNewLicense = true;
      config.buyButton = {
        title: translateFn('Buy new license'),
        loading: this.loadingBuyButton,
        buyEventHandlerFn: this.handleBuyNewLicenseClickEvent.bind(this),
      };
    }

    return config;
  }

  private handleSwitchChanged(event: CustomEvent): void {
    this.switchValue = event.detail.selectedValue;
  }

  private async handleProductChanged(event: CustomEvent) {
    const productId = event.detail.newValue.value;

    if (this.selectedProduct && productId === this.selectedProduct.productId) {
      // do nothing when same product is selected
      return;
    }

    await this.selectProduct(productId, SwitchValue.ExtendSeats);
  }

  private async selectProduct(productId: number, switchValue: SwitchValue) {
    const selectedProduct = this.products.find(p => p.productId === productId);
    if (!selectedProduct) {
      throw new Error(
        '[GzPurchasePageWebComponent] Cannot set selected product because no matching product has been found'
      );
    }

    // reset state variables
    this.showTextForMaxSeatsReached = false;
    this.maxSeatsAlreadyExceeded = false;

    this.selectedProduct = selectedProduct;
    this.setSwitchValueForSelectedProduct(switchValue);

    if (selectedProduct.seats >= selectedProduct.maxSeats) {
      // this can happen if license has been modified directly in SV4 (not through Verifone)
      this.maxSeatsAlreadyExceeded = true;
    } else {
      this.seatsValue = this.getMinSeats();
      await this.fetchAndUpdateSeatsPriceFn();
    }
  }

  private async handleSeatsChange(event: CustomEvent) {
    const newSeatsValue = event.detail.value;
    this.seatsValue = newSeatsValue;

    if (newSeatsValue < this.getMaxSeats()) {
      this.showTextForMaxSeatsReached = false;
    }

    // show loading widget immediately (ignore debounce)
    this.loadingExtendSeatsPrice = true;

    this.fetchAndUpdateSeatsFnDebounced();
  }

  private async handleMaxValueReached(event: CustomEvent) {
    this.showTextForMaxSeatsReached = true;
  }

  public async setProducts(products: PurchasePageProduct[]) {
    this.products = products;

    if (this.products.length === 0) {
      return;
    }

    // reset field if there are products
    this.noExtendableProductsReason = undefined;

    if (this.productIdToSelectAfterLoad) {
      await this.selectProduct(this.productIdToSelectAfterLoad.productId, this.productIdToSelectAfterLoad.switchValue);
      delete this.productIdToSelectAfterLoad;
    } else {
      await this.selectProduct(products[0].productId, SwitchValue.ExtendSeats);
    }
  }

  public setProductsToBuy(products: AvailableInProductTrial[]) {
    this.purchasableProducts = products;
  }

  public async setSelectedProduct(productId: number, switchValue: SwitchValue) {
    await this.selectProduct(productId, switchValue);
  }

  public setProductToBeSelectedAfterLoad(productId: number, switchValue: SwitchValue) {
    this.productIdToSelectAfterLoad = {
      productId: productId,
      switchValue: switchValue,
    };
  }

  private getMinSeats() {
    return 1;
  }

  private getMaxSeats() {
    return this.selectedProduct.maxSeats - this.selectedProduct.seats;
  }

  private getTotalSeats() {
    return this.selectedProduct.seats + this.seatsValue;
  }

  private computeServerSeats(totalSeats: number): number | undefined {
    if (!this.selectedProduct.serversPercentage) {
      return undefined;
    }

    const serverPercentage = this.selectedProduct.serversPercentage;

    return Math.ceil((totalSeats * serverPercentage) / 100);
  }

  private getServerSeatsPercentage() {
    return this.selectedProduct.serversPercentage || 0;
  }

  private async handleExtendSeatsBuyEvent() {
    this.loadingBuyButton = true;

    const response = await this.purchasePageDataProvider.getExtendSeatsUrl(
      this.selectedProduct.licenseKey,
      this.getTotalSeats()
    );

    this.loadingBuyButton = false;

    window.open(response.url, '_blank');

    this.setShowRefreshAfterPurchaseInfoBox();
  }

  private async handleExtendPeriodBuyEvent() {
    this.loadingBuyButton = true;

    const response = await this.purchasePageDataProvider.getRenewalUrl(this.selectedProduct.licenseKey);

    this.loadingBuyButton = false;

    window.open(response.url, '_blank');

    this.setShowRefreshAfterPurchaseInfoBox();
  }

  private async handleBuyNewLicenseClickEvent() {
    const buyUrl = BUY_NEW_LICENSE_WINBACK_LINKS[this.selectedProduct.productId];

    if (buyUrl) {
      window.open(buyUrl, '_blank');
    }
  }

  private setShowRefreshAfterPurchaseInfoBox() {
    this.showRefreshAfterPurchaseInfoBox = true;

    this.scrollTo({ behavior: 'smooth', top: 0 });
  }

  private createFetchAndUpdateSeatsPriceFn() {
    return async () => {
      this.loadingExtendSeatsPrice = true;

      const currentRequestId = this.lastFetchSeatsPriceRequestId + 1;
      this.lastFetchSeatsPriceRequestId = currentRequestId;

      const getExtendSeatsPriceResponse = await this.purchasePageDataProvider.getExtendSeatsPrice(
        this.selectedProduct.licenseKey,
        this.getTotalSeats()
      );

      if (this.lastFetchSeatsPriceRequestId > currentRequestId) {
        // not the last request, ignore result
        return;
      }

      this.selectedProduct = {
        ...this.selectedProduct,
        extendSeatsPrice: getExtendSeatsPriceResponse.price,
        currency: getExtendSeatsPriceResponse.currency,
      };

      this.loadingExtendSeatsPrice = false;
    };
  }

  private setSwitchValueForSelectedProduct(value: SwitchValue) {
    // cannot extend seats for expired licenses
    this.switchValue = this.selectedProduct.isExpired ? SwitchValue.ExtendPeriod : value;
  }

  private renderMaxSeatsAlreadyExceededText(): TemplateResult | undefined {
    const selectedProduct = this.selectedProduct;
    const pageUrl = MAX_SEATS_EXCEEDED_PRODUCT_PAGES[selectedProduct.productId];

    return html`
      <gz-text class="mt-2" .fontSize=${FontSizes.PARAGRAPH_12} automationId="ipb-extend-seats-max-limit-reached-text">
        ${translateFn(
          'You can increase the number of your license seats up to {MAX_SEATS}. If you would like to extend your license further, purchase an additional license key for the same product and contact us at b2bcommercial@bitdefender.com.',
          {
            MAX_SEATS: selectedProduct.maxSeats,
          }
        )}
      </gz-text>
      ${pageUrl
        ? html`
            <gz-link class="mt-1 no-padding" .href=${pageUrl} automationId="ipb-extend-seats-max-limit-reached-link">
              <gz-text .fontSize=${FontSizes.PARAGRAPH_12}>${translateFn('Purchase an additional key')}</gz-text>
            </gz-link>
          `
        : ''}
    `;
  }

  public setNoExtendableProductsReason(noExtendableProductsReason: NoExtendableProductsReason) {
    this.noExtendableProductsReason = noExtendableProductsReason;
  }

  private translateNoExtendableProductsReason(noExtendableProductsReason: NoExtendableProductsReason) {
    switch (noExtendableProductsReason) {
      case NoExtendableProductsReason.TrialInProgress:
        return translateFn('Expanding your plan is not available during a product trial. Cancel the trial or purchase the product to continue.');
      case NoExtendableProductsReason.DisabledSubscription:
        return translateFn(
          'Your current license is linked to a disabled billing account. Reactivate your account before proceeding.'
        );
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-purchase-page': GzPurchasePageWebComponent;
  }
}
